import React, { useEffect } from 'react';
import { Trophy, Flame, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import confetti from 'canvas-confetti';

/**
 * Streak milestone celebration modal.
 *
 * Shown once per milestone per user (caller is responsible for the once-only
 * guard via localStorage). Fires a stronger confetti burst than the daily
 * 4/4 reward — milestones are rare events.
 */
const MILESTONE_BADGES = {
  3: { emoji: '🔥', label: '3-day streak', sub: "You're building a real habit." },
  7: { emoji: '⭐', label: '1 week streak', sub: 'Seven days of showing up.' },
  14: { emoji: '🌟', label: '2 week streak', sub: "You're unstoppable." },
  21: { emoji: '💎', label: '21-day streak', sub: 'Habit formed. Life-changing.' },
  30: { emoji: '👑', label: '30-day streak', sub: "You're an inspiration." },
  60: { emoji: '🏅', label: '60-day streak', sub: 'Master of consistency.' },
  90: { emoji: '🏆', label: '90-day streak', sub: 'Health warrior status.' },
  180: { emoji: '🌈', label: '180-day streak', sub: 'Half a year of excellence.' },
  365: { emoji: '🎉', label: 'ONE YEAR', sub: 'Absolute legend.' },
};

const fireMilestoneConfetti = () => {
  try {
    const colors = ['#10b981', '#14b8a6', '#a855f7', '#f59e0b', '#fbbf24'];
    const end = Date.now() + 1400;
    (function frame() {
      confetti({
        particleCount: 6,
        angle: 60,
        spread: 70,
        startVelocity: 55,
        origin: { x: 0, y: 0.65 },
        colors,
        scalar: 0.9,
        gravity: 1,
        ticks: 200,
        disableForReducedMotion: true,
      });
      confetti({
        particleCount: 6,
        angle: 120,
        spread: 70,
        startVelocity: 55,
        origin: { x: 1, y: 0.65 },
        colors,
        scalar: 0.9,
        gravity: 1,
        ticks: 200,
        disableForReducedMotion: true,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    if (navigator.vibrate) navigator.vibrate([60, 40, 100, 40, 120]);
  } catch (_) { /* ignore */ }
};

export const MilestoneModal = ({ streak, onClose }) => {
  const meta = MILESTONE_BADGES[streak];

  useEffect(() => {
    if (meta) fireMilestoneConfetti();
  }, [meta]);

  if (!meta) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-6"
      onClick={onClose}
      data-testid="milestone-modal-overlay"
    >
      <div
        className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full p-7 text-center"
        onClick={(e) => e.stopPropagation()}
        data-testid="milestone-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close milestone celebration"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          data-testid="milestone-modal-close-btn"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mx-auto mb-5 w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 via-orange-400 to-rose-500 flex items-center justify-center shadow-lg">
          <span className="text-4xl" aria-hidden="true">{meta.emoji}</span>
        </div>

        <p className="text-xs font-semibold text-emerald-700 uppercase tracking-widest mb-2">
          Milestone unlocked
        </p>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {meta.label}
        </h2>
        <p className="text-sm text-gray-600 leading-relaxed mb-6">
          {meta.sub}
        </p>

        <div className="flex items-center justify-center gap-2 mb-6 px-4 py-3 rounded-2xl bg-emerald-50 border border-emerald-100">
          <Flame className="h-5 w-5 text-orange-500" />
          <span className="text-sm font-semibold text-gray-900">
            {streak} consecutive days
          </span>
        </div>

        <Button
          onClick={onClose}
          className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl"
          data-testid="milestone-modal-continue-btn"
        >
          <Trophy className="h-4 w-4 mr-2" />
          Keep going
        </Button>
      </div>
    </div>
  );
};

export default MilestoneModal;
